/**
 * Search Progress Indicator
 * Shows detailed progress during vector search operations
 */

import React from 'react';
import { Card } from '@tracekit/core';
import { Search, Brain, Zap, CheckCircle, Loader2 } from 'lucide-react';

interface SearchProgressIndicatorProps {
  isSearching: boolean;
  stage?: 'querying' | 'calculating' | 'ranking' | 'finalizing';
  progress?: number; // 0-100
  message?: string;
}

const stages = {
  querying: {
    label: 'Searching Database',
    icon: Search,
    description: 'Finding matching opportunities...',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
  },
  calculating: {
    label: 'Calculating Similarities',
    icon: Brain,
    description: 'Computing semantic matches...',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
  },
  ranking: {
    label: 'Ranking Results',
    icon: Zap,
    description: 'Sorting by relevance...',
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
  },
  finalizing: {
    label: 'Finalizing',
    icon: CheckCircle,
    description: 'Preparing results...',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
  },
};

export function SearchProgressIndicator({
  isSearching,
  stage = 'querying',
  progress = 0,
  message,
}: SearchProgressIndicatorProps) {
  if (!isSearching) return null;

  const currentStage = stages[stage];
  const StageIcon = currentStage.icon;

  // Estimate progress based on stage if not provided
  const estimatedProgress = progress || (
    stage === 'querying' ? 25 :
    stage === 'calculating' ? 60 :
    stage === 'ranking' ? 85 :
    95
  );

  return (
    <Card className={`p-4 mb-4 border-2 ${currentStage.bgColor} border-current`}>
      <div className="space-y-3">
        {/* Stage Header */}
        <div className="flex items-center gap-3">
          <div className={`relative ${currentStage.color}`}>
            <StageIcon className="h-5 w-5 animate-pulse" />
            <Loader2 className="absolute inset-0 h-5 w-5 animate-spin opacity-50" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className={`font-semibold ${currentStage.color}`}>
                {currentStage.label}
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {estimatedProgress}%
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {message || currentStage.description}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full ${currentStage.bgColor} transition-all duration-500 ease-out relative overflow-hidden`}
            style={{ width: `${estimatedProgress}%` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-pulse"></div>
          </div>
        </div>

        {/* Stage Indicators */}
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          {Object.entries(stages).map(([key, stageInfo]) => {
            const Icon = stageInfo.icon;
            const isActive = key === stage;
            const isCompleted = Object.keys(stages).indexOf(key) < Object.keys(stages).indexOf(stage);
            
            return (
              <div key={key} className="flex items-center gap-1">
                {isCompleted ? (
                  <CheckCircle className="h-3 w-3 text-green-600 dark:text-green-400" />
                ) : isActive ? (
                  <Icon className={`h-3 w-3 ${stageInfo.color} animate-pulse`} />
                ) : (
                  <Icon className="h-3 w-3 text-gray-300 dark:text-gray-600" />
                )}
                <span className={isActive ? 'font-medium' : ''}>
                  {stageInfo.label.split(' ')[0]}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
